import { computed, toValue, type MaybeRefOrGetter } from "vue";

interface UsePaymentAdapterSelectionOptions {
	/** Preferred default when no valid persisted value exists (e.g. the
	 * locale's `paymentAdapter`). Falls back to the first list entry.
	 * Accepts a ref/getter: the default moves with the customer type. */
	defaultAdapter?: MaybeRefOrGetter<string | undefined>;
}

/**
 * Owns the shopper's payment-adapter choice in checkout: which adapter is
 * selected, persisted across navigation in `norceStore` (persisted state) so
 * returning to checkout keeps the choice. Just state — the switch itself
 * (delete order → recreate → remount) lives in Checkout.vue where the order
 * lifecycle already is.
 *
 * `adapters` is reactive because the list is per customer type (each NCO
 * channel enables its own providers), so it changes when the shopper switches
 * between person and organization.
 *
 * `selected` is a writable computed over `norceStore.paymentAdapter`, so
 * reading it drives the mounted component's `:key`/`:is` and writing it
 * persists the choice.
 */
export const usePaymentAdapterSelection = (
	adapters: MaybeRefOrGetter<string[]>,
	options?: UsePaymentAdapterSelectionOptions
) => {
	const norceStore = useNorceStore();
	const list = () => toValue(adapters);

	const resolveDefault = () => {
		const entries = list();
		const preferred = toValue(options?.defaultAdapter);
		if (preferred && entries.includes(preferred)) {
			return preferred;
		}
		return entries[0] ?? "";
	};

	/**
	 * Point the selection at a value the current list actually offers, and
	 * return it. Called once at setup, and again by Checkout.vue whenever the
	 * list may have changed under it (a customer-type switch, or an order loaded
	 * with the other type) — so a choice belonging to the other customer type
	 * never survives.
	 *
	 * With no list configured the feature is off, so the store is left untouched.
	 *
	 * `force` re-applies the default even when the stored value is still valid:
	 * used on a customer-type switch, where the new type's own default must win
	 * over a selection carried over from the previous type. Without it, an
	 * adapter present in both lists would silently stay selected across a switch.
	 */
	const normalize = (force = false) => {
		const entries = list();
		if (!entries.length) return norceStore.paymentAdapter;
		if (
			force ||
			!norceStore.paymentAdapter ||
			!entries.includes(norceStore.paymentAdapter)
		) {
			norceStore.setPaymentAdapter(resolveDefault());
		}
		return norceStore.paymentAdapter;
	};

	normalize();

	const selected = computed<string>({
		get: () => norceStore.paymentAdapter,
		set: (value) => norceStore.setPaymentAdapter(value)
	});

	return { selected, normalize };
};
